import React, { useState, useEffect, useRef, useCallback} from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  TextField
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import ActionButtons from './ActionButtons';
import useInlineComments from '../../hooks/useInlineComments';
import CommentThreadPanel from '../comments/CommentThreadPanel';
import TextSelectionPopup from '../comments/TextSelectionPopup';
import MarkdownWithMermaid from '../common/MarkdownWithMermaid';
import useValidation from '../../hooks/useValidation';
import TaskApprovalBar from './TaskApprovalBar';
import { ValidatedIcon, UnvalidatedIcon } from '../icons/ValidationIcons';
import useFullScreen from '../../hooks/useFullScreen';
import TopBarButtons from '../atoms/TopBarButtons';
import SectionHeader from '../atoms/Header';
import LoadMoreButton from '../atoms/LoadMore';
import ConfirmationDialog from '../common/ConfirmationDialog';
import VersionSelector from '../atoms/VersionSelector';
import ExplanationModal from '../atoms/ExplanationModal';
import ImprovementModal from '../atoms/ImprovementModal';
import AddSectionModal from '../atoms/AddSectionModal';
import LoadingBox from '../atoms/LoadingBox';
import Accordion from '../atoms/Accordion';
import { EditableTextField } from '../atoms';
import useDynamicSections from '../../hooks/useDynamicSections';
import useSectionCRUD from '../../hooks/useSectionCRUD';
import useContentActions from '../../hooks/useContentActions';
import useNotification from '../../hooks/useNotification';
import useExplain from '../../hooks/useExplain';
import useImprove from '../../hooks/useImprove';
import InlineImproveInput from '../common/InlineImproveInput';
import usePerformanceTestingVersions from '../../hooks/usePerformanceTestingVersions';
import EmptyContentPlaceholder from '../atoms/EmptyContentPlaceholder';
import conversationService from '../../services/conversationService';
import MarkdownRenderer from '../common/MarkdownRenderer';
import useParsedSections from '../../hooks/useParsedSections';
import { parsePerformanceTestingSections } from '../../utils/markdownSectionParser';
import mermaid from 'mermaid';

// Initialize mermaid config
try {
  mermaid.initialize({
    startOnLoad: false,
    theme: 'default',
    securityLevel: 'loose',
    flowchart: { useMaxWidth: false, htmlLabels: true },
    sequence: { diagramMarginX: 50, diagramMarginY: 10 },
    classDiagram: { useMaxWidth: false },
    er: { useMaxWidth: false },
    gantt: { useMaxWidth: false }
  });
} catch (error) {
  console.error('Error initializing mermaid:', error);
}


/**
 * A split-view layout component that shows conversation on the left
 * and dynamically rendered performance testing content on the right with accordion sections
 */
const SplitViewPerformanceTesting = ({ conversation, children, isStreaming, streamingContent, projectId, onConversationUpdate }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [visibleSections, setVisibleSections] = useState(10);
  const { showNotification } = useNotification();
  const testingPanelRef = useRef(null);
  const isLocalUpdateRef = useRef(false);
  const prevVersionIndexRef = useRef(null);

  // Version management
  const {
    versions,
    currentVersion,
    currentVersionIndex,
    setCurrentVersionIndex,
    hasVersions,
    isLatestVersion,
    updateCurrentVersion
  } = usePerformanceTestingVersions(conversation, isStreaming, streamingContent, isLocalUpdateRef);

  // Use the common hooks for dynamic sections with custom parser
  const {
    sections: dynamicSections,
    setSections,
    expandedSections,
    setExpandedSections,
    handleSectionToggle,
    contentRef: dynamicContentRef,
    hasContent: dynamicHasContent,
    isMatchingConversation: isPerformanceTestingConversation
  } = useDynamicSections(conversation, isStreaming, streamingContent, 'performance-testing', parsePerformanceTestingSections);

  // Section highlight from search navigation (?section=sectionId)
  const useSectionHighlight = require('../../hooks/useSectionHighlight').default;
  const { highlightRef, shouldHighlight } = useSectionHighlight(expandedSections, setExpandedSections);


  // useParsedSections hook — single source of truth for section data
  const {
    sections: parsedSections,
    setSections: setParsedSections,
    groupedSections,
    isLoading: isSectionsLoading,
    contentRef,
    refresh: refreshSections,
    markLocalEdit,
  } = useParsedSections({ conversation, isStreaming, streamingContent });

  // Version switching: reset visible sections when switching versions
  useEffect(() => {
    if (currentVersion) {
      if (prevVersionIndexRef.current !== currentVersionIndex) {
        setVisibleSections(10);
        prevVersionIndexRef.current = currentVersionIndex;
      }
    }
  }, [currentVersion, currentVersionIndex]);

  // Determine which sections to use
  const sections = parsedSections.length > 0 ? parsedSections : dynamicSections;
  const hasContent = sections.length > 0 || dynamicHasContent;

  // Use CRUD operations hook with version support
  const {
    editingSection,
    editedContent,
    editedTitle,
    isSaving,
    deleteConfirmation,
    setEditedContent,
    setEditedTitle,
    setDeleteConfirmation,
    handleStartEdit,
    handleCancelEdit,
    handleSaveEdit,
    handleDeleteSection,
    handleDeleteSectionConfirmed,
    handleCopySection
  } = useSectionCRUD(
    conversation,
    sections,
    setParsedSections,
    setExpandedSections,
    contentRef,
    updateCurrentVersion,
    currentVersion,
    isLocalUpdateRef,
    onConversationUpdate,
    markLocalEdit
  );

  // Use content actions hook
  const {
    isExporting,
    isSaving: isSavingDocument,
    handleExport,
    handleCopy,
    handleRefreshDiagrams,
    handleSaveToDocument,
    getExportContent,
  } = useContentActions(contentRef, 'Performance Testing', projectId, conversation?._id);

  // Full screen functionality
  const {
    isFullScreen,
    toggleFullScreen,
    getLeftPanelStyles,
    getRightPanelStyles,
    getContentStyles
  } = useFullScreen();

  // Initialize validation state for sections
  const initialValidationState = sections.reduce((acc, section) => {
    acc[section.id] = false;
    return acc;
  }, {});

  // Use the validation hook
  const {
    validatedItems: validatedSections,
    handleValidateItem: handleValidateSection,
    isLoading: isLoadingValidations,
    currentUserMemberType,
    isAssignedValidator,
    getValidatorType,
  } = useValidation({
    conversationId: conversation?._id,
    projectId,
    taskId: 'performance-testing',
    initialState: initialValidationState
  });

  // Section content refs for text selection
  const sectionContentRefs = React.useRef({});
  const getSectionContentRef = (sectionId) => {
    if (!sectionContentRefs.current[sectionId]) {
      sectionContentRefs.current[sectionId] = { current: null };
    }
    return sectionContentRefs.current[sectionId];
  };

  // Inline comments
  const {
    commentCounts,
    sectionComments,
    openSectionId: commentOpenSectionId,
    openCommentPanel,
    closeCommentPanel,
    addComment,
    replyToComment,
    resolveComment: resolveCommentAction,
    reopenComment: reopenCommentAction,
    deleteComment: deleteCommentAction,
    isLoading: isCommentsLoading,
  } = useInlineComments({
    projectId,
    conversationId: conversation?._id,
    taskId: 'performance-testing',
  });

  // Use explain hook
  const {
    isExplaining,
    explanationContent,
    showExplanationModal,
    currentSection: explainSection,
    requestExplanation,
    closeExplanationModal
  } = useExplain(projectId);

  // Improve functionality
  const {
    isImproving,
    improvedContent,
    showImprovementModal,
    currentSection: improveSection,
    showImproveInput,
    improvePrompt,
    setImprovePrompt,
    handleImproveClick,
    handleSendImprove,
    handleCancelImprove,
    closeImprovementModal,
    isSpecificSectionImprovement,
    specificSectionName
  } = useImprove(projectId);

  // Handler for applying improved content
  const handleUpdateImprovedContent = async () => {
    if (!improveSection || !improvedContent) return;
    try {
      if (improveSection.action === 'remove') {
        
      // Persist to ParsedResponse so changes survive refresh
      try {
        const ts = conversation?.messages?.find(m => m.role === 'assistant')?.createdAt;
        if (ts) {
          const conversationService = (await import('../../services/conversationService')).default;
          await conversationService.updateParsedSections(
            conversation._id || conversation.id,
            ts,
            [] // empty — backend will reconstruct from the saved conversation message
          );
        }
      } catch (e) { console.warn('ParsedResponse save failed:', e); }

      closeImprovementModal();
        showNotification({ message: `Removed: ${improveSection.title}`, type: 'success' });
        if (typeof onConversationUpdate === 'function') onConversationUpdate();
        return;
      }
      closeImprovementModal();
      showNotification({ message: 'Section improved successfully', type: 'success' });
    } catch (error) {
      showNotification({ message: 'Failed to apply improvement', type: 'error' });
    }
  };

  const handleExplainSection = async (section) => {
    await requestExplanation(section, conversation?._id);
  };

  // Handle version change
  const handleVersionChange = (newIndex) => {
    console.log('🔄 Version change requested:', {
      from: currentVersionIndex,
      to: newIndex,
      versionsCount: versions.length
    });

    // Reset the local update flag when user manually switches versions
    isLocalUpdateRef.current = false;
    setCurrentVersionIndex(newIndex);

    // Show notification
    showNotification({
      message: `Switched to Version ${newIndex + 1}`,
      type: "info"
    });
  };

  // Handle adding new section
  const handleAddSection = async (formData) => {
    try {
      console.log('➕ Adding new performance test section:', formData);

      // Generate unique ID for the new section
      const newSectionId = `perf-test-${Date.now()}`;

      // Create the new section
      const newSection = {
        id: newSectionId,
        title: formData.title,
        content: formData.generatedContent || '',
        rawContent: formData.generatedContent || '',
        category: formData.category || 'Custom Tests',
        isFirstInCategory: false
      };

      // Add to existing sections
      const updatedSections = [...sections, newSection];

      // Recalculate isFirstInCategory flags
      let lastSeenCategory = null;
      updatedSections.forEach(section => {
        if (section.category && section.category !== lastSeenCategory) {
          section.isFirstInCategory = true;
          lastSeenCategory = section.category;
        } else {
          section.isFirstInCategory = false;
        }
      });

      // Set the flag to prevent fetch from overwriting local edit
      markLocalEdit();
      isLocalUpdateRef.current = true;

      setSections(updatedSections);
      setExpandedSections(prev => ({ ...prev, [newSectionId]: true }));

      // Reconstruct the full content
      let updatedContent = '';
      updatedSections.forEach((section, idx) => {
        if (idx > 0) updatedContent += '\n\n';

        if (section.category && section.isFirstInCategory) {
          updatedContent += `## ${section.category}\n\n`;
        }
        updatedContent += `### ${section.title}\n\n${section.content}`;
      });

      // Update content ref
      contentRef.current = updatedContent.trim();

      // Update the current version
      if (updateCurrentVersion && currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          content: updatedContent.trim(),
          testCases: updatedSections,
          parsedContent: updatedSections
        });
      }

      // Save to backend
      await saveToBackend(updatedContent.trim());

      showNotification({
        message: `"${formData.title}" added successfully`,
        type: "success"
      });

      // Close modal
      setShowAddModal(false);
    } catch (error) {
      console.error('Error adding section:', error);
      showNotification({
        message: "Failed to add section",
        type: "error"
      });
    }
  };

  // Save to backend helper
  const saveToBackend = async (updatedContent) => {
    const updatedMessages = [...conversation.messages];

    console.log('🔍 saveToBackend called with:', {
      currentVersionId: currentVersion?.id,
      currentVersionNumber: currentVersion?.versionNumber,
      currentVersionTimestamp: currentVersion?.timestamp,
      totalMessages: updatedMessages.length,
      messages: updatedMessages.map((m, idx) => ({
        index: idx,
        role: m.role,
        createdAt: m.createdAt,
        hasContent: !!m.content
      }))
    });

    // Find and update the message for current version
    // Match by timestamp since backend doesn't return message IDs
    let messageUpdated = false;
    if (currentVersion?.timestamp) {
      for (let i = 0; i < updatedMessages.length; i++) {
        const msg = updatedMessages[i];
        // Match by timestamp and role
        if (msg.role === 'assistant' && msg.createdAt === currentVersion.timestamp) {
          console.log('✅ Found matching message at index', i, 'with timestamp:', msg.createdAt);
          updatedMessages[i] = {
            ...updatedMessages[i],
            content: updatedContent
          };
          messageUpdated = true;
          break;
        }
      }
    }

    // Fallback: match by version number (nth assistant message)
    if (!messageUpdated && currentVersion?.versionNumber) {
      console.warn('⚠️ No timestamp match found, trying to match by version number');
      let assistantMessageCount = 0;
      for (let i = 0; i < updatedMessages.length; i++) {
        if (updatedMessages[i].role === 'assistant') {
          assistantMessageCount++;
          if (assistantMessageCount === currentVersion.versionNumber) {
            console.log('✅ Found matching message by version number at index', i);
            updatedMessages[i] = {
              ...updatedMessages[i],
              content: updatedContent
            };
            messageUpdated = true;
            break;
          }
        }
      }
    }

    if (!messageUpdated) {
      console.error('❌ Could not find matching message to update!');
    }

    // Prepare conversation data
    const conversationData = {
      messages: updatedMessages.map(msg => ({
        role: msg.role,
        content: msg.content,
        attachments: msg.attachments || [],
        createdAt: msg.createdAt || new Date().toISOString()
      }))
    };

    // Save to backend
    await conversationService.saveConversation(conversation._id, conversationData);

    // Notify parent component to refresh conversation (updates left panel)
    if (onConversationUpdate) {
      await onConversationUpdate(conversation._id);
    }
  };

  // Handle load more
  const handleLoadMore = () => {
    setVisibleSections(prev => Math.min(prev + 10, sections.length));
  };

  // Handle show all
  const handleShowAll = () => {
    setVisibleSections(sections.length);
  };

  console.log('SplitViewPerformanceTesting rendering', {
    conversationId: conversation?._id,
    taskId: conversation?.taskId,
    isPerformanceTestingConversation,
    isStreaming,
    currentVersionId: currentVersion?.id,
    sectionsCount: sections.length,
    hasVersions,
    versionsCount: versions?.length
  });


  // Auto-scroll panel to bottom when streaming content updates
  useEffect(() => {
    if (isStreaming && testingPanelRef.current && isPerformanceTestingConversation) {
      setTimeout(() => {
        if (testingPanelRef.current) {
          testingPanelRef.current.scrollTop = testingPanelRef.current.scrollHeight;
        }
      }, 50);
    }
  }, [isStreaming, isPerformanceTestingConversation]);


  // Don't show the split view if this isn't a performance testing conversation or if there's no content

  if (!isPerformanceTestingConversation || (!hasContent && !isStreaming)) {
    console.log('Not showing split view: either not performance testing conversation or no content available');
    return <>{children}</>;
  }

  console.log('Showing split view with performance testing content', { hasContent, isStreaming });

  // Render the split view
  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      height: '100%',
      mt: 5
    }}>
      {/* Main content with conditional split */}
      <Box sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        flexDirection: { xs: 'column', md: 'row' },
        flex: 1,
        overflow: 'hidden'
      }}>
        {/* Main conversation panel */}
        <Box sx={{
          ...getLeftPanelStyles(sidebarCollapsed),
          p: 2,
        }}>
          {children}
        </Box>

        {/* Performance testing panel */}
        <Box
          ref={testingPanelRef}
          sx={{
            ...getRightPanelStyles(sidebarCollapsed),
            backgroundColor: '#f7f7f7',
            height: '97%',
            p: 2
          }}
        >
          <Paper
            elevation={0}
            sx={{
              ...getContentStyles({}),
              borderRadius: "8px",
              backgroundColor: 'white',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 10,
            }}>
              <SectionHeader label='Performance testing' />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>

                {/* Version selector */}
                {hasVersions && versions.length > 1 && (
                  <VersionSelector
                    versions={versions}
                    currentVersionIndex={currentVersionIndex}
                    onVersionChange={handleVersionChange}
                    disabled={isStreaming}
                  />
                )}

                {/* Fixed header section */}
                <TopBarButtons
                  isFullScreen={isFullScreen}
                  onToggleFullScreen={toggleFullScreen}
                  onAdd={isLatestVersion ? () => setShowAddModal(true) : null}
                  getExportContent={getExportContent}
                  onExport={handleExport}
                  isExporting={isExporting}
                  onCopy={handleCopy}
                  onRefresh={handleRefreshDiagrams}
                  onSave={projectId ? () => handleSaveToDocument('PERFORMANCE_TESTING') : undefined}
                  isSaving={isSaving || isSavingDocument}
                  disabled={!contentRef.current}
                  addTooltip={!isLatestVersion ? "Switch to latest version to add tests" : "Add performance test section"}
                  exportTooltip="Export performance testing as markdown"
                  copyTooltip="Copy performance testing to clipboard"
                  refreshTooltip="Refresh diagrams"
                  saveTooltip="Save as document"
                />
              </Box>
            </Box>


            {/* Task-level approval tracking */}
            <TaskApprovalBar
              projectId={projectId}
              taskId={'performance-testing'}
              conversationId={conversation?._id || conversation?.id}
              validatedItems={validatedSections}
              totalItems={parsedSections?.length || 0}
                currentUserMemberType={currentUserMemberType}
            />
            <Box sx={{
              p: 3,
              pt: 2,
              flex: 1,
              overflow: "auto",
              '&::-webkit-scrollbar': {
                width: '8px',
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-track': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                marginTop: '5px',
                marginBottom: '5px',
              },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.4)',
                },
              },
              '&::-webkit-scrollbar-corner': {
                backgroundColor: 'transparent',
              },
            }}>
              {/* Show performance testing content or a placeholder if none found yet */}
              {isStreaming && isLatestVersion ? (
                <LoadingBox message="Generating performance testing content..." spinnerSize={40} />
              ) : sections.length > 0 ? (
                <Box>
                  {/* Render sections grouped by category */}
                  {(() => {
                    const groups = groupedSections;
                    let globalIndex = 0;
                    return groups.slice(0, visibleSections).map((group, groupIndex) => (
                      <Box key={group.categoryId} sx={{ mb: 3 }}>
                        {group.category && (
                          <Box sx={{ mt: groupIndex === 0 ? 0 : 2, mb: 1.5 }}>
                            <SectionHeader label={group.category} />
                          </Box>
                        )}
                        {group.categorySection && (
                          <Box sx={{ pl: 2, mb: 2 }}>
                            <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                              <MarkdownRenderer content={group.categorySection.content || ''} />
                            </Box>
                          </Box>
                        )}
                        <Box sx={{ pl: group.category ? 2 : 0 }}>
                          {group.sections.map((section) => {
                            const idx = globalIndex++;
                            return (
                    <React.Fragment key={`accordion-${section.id}`}>
                    <Accordion
                      ref={shouldHighlight(section.id) ? highlightRef : null}
                      title={section.title}
                      expanded={expandedSections[section.id] || false}
                      onChange={() => handleSectionToggle(section.id)}
                      titleContent={editingSection === section.id ? (
                        <Box title="" onClick={(e) => e.stopPropagation()} sx={{ maxWidth: '93%', width: '93%' }}>
                          <TextField
                            value={editedTitle}
                            onChange={(e) => setEditedTitle(e.target.value)}
                            onKeyDown={(e) => e.stopPropagation()}
                            onFocus={(e) => e.target.removeAttribute('title')}
                            size="small"
                            fullWidth
                            placeholder="Enter performance test title"
                            variant="outlined"
                            InputProps={{ title: '' }}
                            inputProps={{ title: '', autoComplete: 'off' }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                fontSize: '14px',
                                fontFamily: 'inherit',
                                backgroundColor: '#fff',
                                '& fieldset': { borderWidth: '1px' },
                                '&:hover fieldset': { borderWidth: '1px' },
                                '&.Mui-focused fieldset': { borderWidth: '1px' },
                              },
                              '& .MuiOutlinedInput-input': {
                                fontSize: '14px',
                                fontFamily: 'inherit',
                                padding: '8px 12px',
                              },
                            }}
                          />
                        </Box>
                      ) : null}
                      rightIcon={
                        <Box
                          onClick={async (e) => {
                            e.stopPropagation();
                            const isCurrentlyValidated = validatedSections[section.id];

                            try {
                              await handleValidateSection(section.id);

                              const versionInfo = currentVersion && !currentVersion.isPreview
                                ? ` (Version ${currentVersion.versionNumber})`
                                : '';

                              if (isCurrentlyValidated) {
                                showNotification({
                                  message: `Validation removed for "${section.title}"${versionInfo}`,
                                  type: "error"
                                });
                              } else {
                                showNotification({
                                  message: `"${section.title}" validated successfully${versionInfo}`,
                                  type: "success"
                                });
                              }
                            } catch (error) {
                              showNotification({
                                message: `Failed to update validation for "${section.title}"`,
                                type: "error"
                              });
                            }
                          }}
                          sx={{ display: 'flex', alignItems: 'center', cursor: isAssignedValidator ? 'pointer' : 'default', opacity: isAssignedValidator ? 1 : 0.4, pointerEvents: isAssignedValidator ? 'auto' : 'none' }}
                        >
                          {validatedSections[section.id] ? (
                            <ValidatedIcon size={25} memberType={getValidatorType(section.id)} />
                          ) : (
                            <UnvalidatedIcon size={25} />
                          )}
                        </Box>
                      }
                      detailsContent={
                        <Box
                          sx={{
                            cursor: editingSection === section.id ? 'default' : 'pointer',
                            position: 'relative'
                          }}
                        >
                          <Box sx={{
                            width: "93%",
                            minHeight: "230px"
                          }}>
                            {editingSection === section.id ? (
                              <EditableTextField
                                value={editedContent}
                                onChange={(e) => setEditedContent(e.target.value)}
                              />
                            ) : (
                              <Box
                            ref={getSectionContentRef(section.id)}
                            sx={{ position: "relative" }}
                          >
                            <MarkdownWithMermaid content={section.content} />
                            <TextSelectionPopup
                                containerRef={getSectionContentRef(section.id)}
                                sectionId={section.id}
                                onSubmitComment={addComment}
                                disabled={editingSection === section.id}

                              />
                          </Box>
                            )}
                          </Box>
                          <Box sx={{
                            position: 'absolute',
                            right: 10,
                            top: 10,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5
                          }}>
                            <ActionButtons
                              item={section}
                              itemIndex={idx}
                              featureId={section.id}
                              editingFeature={editingSection}
                              isSaving={isSaving}
                              onEdit={() => handleStartEdit(section.id, section.content || '', section.title || '')}
                              onSave={() => handleSaveEdit(section.id)}
                              onCancel={handleCancelEdit}
                              onImprove={() => handleImproveClick(section)}
                              onExplain={() => handleExplainSection(section)}
                              onCopy={() => handleCopySection(section)}
                              onDelete={() => handleDeleteSection(section)}
                              vertical={true}
                          onComment={() => openCommentPanel(section.id)}
                          commentCount={commentCounts[section.id]?.open || 0}
                          isApproved={!!validatedSections[section.id]}
                          approvedByType={validatedSections[section.id] ? getValidatorType(section.id) : null}
                            />
                          </Box>
                        </Box>
                      }
                    />
                    {showImproveInput === section.id && (
                      <InlineImproveInput
                        improvePrompt={improvePrompt}
                        onPromptChange={setImprovePrompt}
                        onSend={() => handleSendImprove(section)}
                        onCancel={handleCancelImprove}
                      />
                    )}
                    </React.Fragment>
                            );
                          })}
                        </Box>
                      </Box>
                    ));
                  })()}

                  {/* Load more button for lazy loading */}
                  {(() => {
                    const groups = groupedSections;
                    return groups.length > visibleSections && (
                      <LoadMoreButton
                        remaining={groups.length - visibleSections}
                        onLoadMore={handleLoadMore}
                        onShowAll={handleShowAll}
                        label="Load more sections"
                      />
                    );
                  })()}
                </Box>
              ) : (
                <EmptyContentPlaceholder
                  title="No performance testing content found yet."
                  description="Ask the AI to create comprehensive performance testing strategies for your application. You can use prompts like:"
                  examplePrompt="Can you create performance test cases for load testing, stress testing, and scalability testing?"
                  additionalInfo="You can also ask for specific performance tests like memory profiling, database optimization, and network latency tests."
                />
              )}
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Confirmation dialog for delete */}
      <ConfirmationDialog
        open={deleteConfirmation.open}
        onClose={() => setDeleteConfirmation({ open: false, section: null })}
        onConfirm={handleDeleteSectionConfirmed}
        title="Delete Section"
        message={`Are you sure you want to delete "${deleteConfirmation.section?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        severity="warning"
        height='auto'
      />

      {/* Add section modal */}
      <AddSectionModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddSection}
        sectionType="performance-test-section"
        title="Add Performance Test Section"
        projectId={projectId}
        conversationId={conversation?._id}
      />

      {/* Explanation Modal */}
      <ExplanationModal
        open={showExplanationModal}
        onClose={closeExplanationModal}
        title={explainSection?.title || ''}
        explanation={explanationContent}
        isLoading={isExplaining}
      />

      {/* Improvement Modal */}
      <ImprovementModal
        open={showImprovementModal}
        onClose={closeImprovementModal}
        section={improveSection}
        improvedContent={improvedContent}
        isGenerating={isImproving}
        onUpdate={handleUpdateImprovedContent}
        isSpecificSection={isSpecificSectionImprovement}
        specificSectionName={specificSectionName}
      />
    

      {/* Inline Comment Thread Panel */}
      <CommentThreadPanel
        open={!!commentOpenSectionId}
        onClose={closeCommentPanel}
        sectionTitle={
          (parsedSections || []).find(s => s.id === commentOpenSectionId)?.title || commentOpenSectionId
        }
        comments={sectionComments}
        isLoading={isCommentsLoading}
        onAddComment={(content) => addComment({ sectionId: commentOpenSectionId, content })}
        onReply={replyToComment}
        onResolve={resolveCommentAction}
        onReopen={reopenCommentAction}
        onDelete={deleteCommentAction}
      />
</Box>
  );
};

export default SplitViewPerformanceTesting;
